import { html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { List, constants, MenuEvent, ColorTypes, events } from '@omegagrid/core';
import { ConditionalFormat, ConditionalFormatType } from '@omegagrid/grid';
import { msg } from '@omegagrid/localize';
import rp from '@reporting/rp-app-host';
import { Style as DefStyle } from "@reporting/rp-types/definition";
import { itemStyle, style } from './ruleList.style';
import { NodeSource } from '@omegagrid/tree';

const ruleTypes: Record<string|null, string> = {
	'formula': msg('Formula/Static'),
	'scale': msg('Scale'),
	'bar': msg('Data Bar'),
	'icon': msg('Icon Set'),
};

@customElement('rp-definition-rules')
export class RuleList extends LitElement {

	static styles = [style];

	@property({type: Object})
	conditionalFormats: ConditionalFormat[] = [];

	@query('og-list')
	list: List;

	public onSave: (formats: ConditionalFormat[]) => void;

	connectedCallback() {
		super.connectedCallback();
	}

	updateList = () => {
		this.list.size = this.conditionalFormats.length;
		this.list.renderItems(true);
	}

	moveRuleUp(index: number) {
		if (index == 0) return;
		this.conditionalFormats.splice(index - 1, 0, this.conditionalFormats.splice(index, 1)[0]);
		this.updateList();
	}

	moveRuleDown(index: number) {
		if (index >= this.conditionalFormats.length) return;
		this.conditionalFormats.splice(index + 1, 0, this.conditionalFormats.splice(index, 1)[0]);
		this.updateList();
	}

	deleteRule(index: number) {
		this.conditionalFormats.splice(index, 1);
		this.updateList();
	}

	toggleRuleStop(index: number) {
		const format = this.getFormatByIndex(index);
		format.stop = !format.stop;
		this.updateList();
	}

	addRule() {
		this.conditionalFormats.push({type: 'formula'} as ConditionalFormat);
		this.updateList();
	}

	_onApplyClick = () => {
		if (this.onSave) {
			this.onSave(this.conditionalFormats);
		}
		this.dispatchEvent(new Event('close'));
	}

	getFormatByIndex(index: number) {
		return this.conditionalFormats[index];
	}

	changeType(index: number, type: ConditionalFormatType) {
		const format = this.getFormatByIndex(index);
		format.type = type;
		this.updateList();
	}

	async getPredefinedStyles(): Promise<NodeSource[]> {
		const styles = await rp.ms('definition').get<DefStyle[]>(`styles/R`);
		return styles.map(style => ({
			key: style.id_style.toString(),
			value: style.description
		}));
	}


	_onMenuSelect = (e: MenuEvent, index: number) => {
		switch (e.item.key) {
			case 'stop':
				this.toggleRuleStop(index);
				break;
			case 'moveUp':
				this.moveRuleUp(index);
				break;
			case 'moveDown':
				this.moveRuleDown(index);
				break;
			case 'delete':
				this.deleteRule(index);
				break;
		}
	}

	_onGroupChange = (e: events.ChangeEvent) => {
		console.log('Group changed', e.value);
	}

	_onSwitchChange = (e: events.ChangeEvent) => {
		console.log('Switch changed', e.value);
	}

	_onPredefinedStyleChange = (e: events.ChangeEvent) => {
		console.log('Predefined style changed', e.value);
	}

	_itemRenderer = (_div: HTMLDivElement, index: number) => html`
		<div class="header" style="padding-right: ${constants.SLIDER_SIZE}px">
			<div>${msg('Rule')} #${index + 1}</div>

			<div>
				<og-dropdown-list
					.value = "${this.getFormatByIndex(index).type}"
					.items="${Object.entries(ruleTypes).map(e => ({key: e[0], value: e[1]}))}"
					@change="${(e: events.ChangeEvent) => this.changeType(index, e.value as ConditionalFormatType)}">
				</og-dropdown-list>
			</div>

			<div>
				<og-dropdown-menu
					noCaret
					dynamicWidth
					alignment="right"
					@menu.select="${(e: MenuEvent) => this._onMenuSelect(e, index)}"
					.items="${[{
						key: 'stop',
						value: msg('Stop if true'),
						icon: this.getFormatByIndex(index).stop ? 'square-check' : 'square',
					}, {
						key: 'moveUp',
						value: msg('Move up'),
						icon: 'arrow-up',
					}, {
						key: 'moveDown',
						value: msg('Move down'),
						icon: 'arrow-down',
					}, {
						key: 'delete',
						value: msg('Delete'),
						icon: 'trash',
						color: 'red',
					}]}">
					<div slot="label"><og-icon icon="bars"></og-icon></div>
				</og-dropdown-menu>
			</div>
		</div>
		<div style="padding-right: ${constants.SLIDER_SIZE}px">
			<rp-mf-definition-form-rule
				.format="${this.getFormatByIndex(index)}">
			</rp-mf-definition-form-rule>
		</div>
	`;

	render = () => html`
		<div class="controls p">
			<og-button text="+ ${msg('New')}" color="${ColorTypes.Blue}" @click="${() => this.addRule()}"></og-button>
			<og-button @click="${this._onApplyClick}" color="${ColorTypes.Green}">
				<div slot="content"><og-fa-icon icon="arrow-up-from-bracket"></og-fa-icon> ${msg('Apply')}</div>
			</og-button>
		</div>
		<div class="body">
			<og-list
				disabledSelect
				dynamicItemHeight
				?nativeHeight="${true}"
				itemHeight="170"
				.customStyle=${itemStyle}
				.itemRenderer="${this._itemRenderer}"
				.size="${this.conditionalFormats.length}">
			</og-list>
		</div>
	`;
}
